import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInsightsFilters } from "../hooks/useInsightsFilters";
import { useChartAggregate } from "../hooks/useChartAggregate";
import { useChartTruncation } from "../hooks/useChartTruncation";
import { FilterPanel } from "./FilterPanel";
import { WindowStaleNotice } from "./WindowStaleNotice";
import { EvaluationsChart } from "./EvaluationsChart";
import { RidgelineChart } from "./RidgelineChart";
import { TruncationWarning } from "./TruncationWarning";
import { GroupBySection } from "./GroupBySection";
import { TriageRail } from "./TriageRail";
import { KpiStrip } from "./KpiStrip";
import { SavedViewTabs } from "./SavedViewTabs";
import { useInsightsKpis } from "../hooks/useInsightsKpis";
// Direct import (not the ../../../ui barrel) so jest doesn't transitively pull
// in import.meta-using modules; kept consistent with the chart components.
import { EmptyState } from "../../../ui/EmptyState";
import { ChartSkeleton } from "../../../ui/ChartSkeleton";
import { Skeleton } from "../../../ui/Skeleton";
import { ChartBarIcon } from "@heroicons/react/24/outline";
import type { HeatmapData } from "../utils/chartData";
import {
  bucketRowsToGroupedLinePoints,
  bucketRowsToHeatmapCells,
  bucketRowsToHistograms,
  bucketRowsToLinePoints,
  bucketRowsToUngroupedHeatmap,
  buildCriteriaLabelMap,
  groupColorMap
} from "../utils/chartData";
import { buildDrillDownTarget } from "../utils/drilldown";
import type { DrillClick } from "../utils/drilldown";
import { deriveIssues } from "../utils/deriveIssues";
import {
  INSIGHTS_NAMESPACE,
  seedTracesFilters,
} from "../utils/filterPersistence";
import { useTagAliases } from "../../tag-aliases/context";
import {
  ATTENTION_FAIL_RATE_CRITICAL,
  ATTENTION_FAIL_RATE_WATCH,
  ATTENTION_SCORE_THRESHOLD,
  DEFAULT_GROUP_LIMIT,
  MIN_ISSUE_EVALUATIONS,
} from "../constants";
import { DOCS_LINK } from "../../../lib/constants";

const EMPTY_HEATMAP: HeatmapData = {
  criteria: [],
  groups: [],
  cells: new Map(),
};

/**
 * Container for the /insights view: filters + aggregate-backed charts.
 */
export function EvaluationsView() {
  const {
    metadataKeys,
    metadataValues,
    loadingMetadataKeys,
    loadMetadataValuesForKey,
    filters,
    addFilter,
    removeFilter,
    updateFilter,
    applyView,
    criteriaFilter,
    setCriteriaFilter,
    dateWindow,
    setDateWindow,
    dateRangeFilter,
    isWindowStale,
    refreshWindow,
    scoreFilter,
    setScoreFilter,
    groupByKey,
    setGroupByKey,
    scoreMetric,
    setScoreMetric,
    criteria,
    loadingCriteria,
    loadCriteria,
    metadataError,
  } = useInsightsFilters();

  const {
    response,
    loading: aggregateLoading,
    error: aggregateError,
  } = useChartAggregate({
    filters,
    criteriaFilter,
    dateRangeFilter,
    scoreFilter,
    histogram: true, // the score-distribution charts bin scores; one fetch
    belowThreshold: ATTENTION_SCORE_THRESHOLD, // triage's failure count
  });

  // How many tag values the grouped section renders (#1414). View-local rather
  // than part of the persisted/shared filter slice: it's a "show me more of what
  // I'm already looking at" control, not something that changes which data the
  // view describes.
  const [groupLimit, setGroupLimit] = useState(DEFAULT_GROUP_LIMIT);

  // Separate grouped fetch for the group-by section; skipped until a key is set.
  const {
    response: groupedResponse,
    loading: groupedLoading,
    error: groupedError,
  } = useChartAggregate({
    filters,
    criteriaFilter,
    dateRangeFilter,
    scoreFilter,
    groupBy: groupByKey,
    groupLimit,
    // Triage judges a segment on how many of its results failed, so the grouped
    // cells need the same failure count the ungrouped ones get — otherwise
    // choosing a group-by would silently switch the rail back to comparing means.
    // No histogram: nothing in the grouped view bins scores, and this response is
    // the heaviest the page can ask for (criteria x groups x buckets rows), so the
    // ten bins per row it used to carry were pure overhead (#1493).
    belowThreshold: ATTENTION_SCORE_THRESHOLD,
    skip: !groupByKey,
  });

  const truncation = useChartTruncation({ response, loading: aggregateLoading });
  const groupedTruncation = useChartTruncation({
    response: groupedResponse,
    loading: groupedLoading,
  });

  // Which metric the Score Trends lines plot (avg | p25 | p10) comes from the
  // persisted filter hook so the toggle survives navigation. All metrics are in
  // the same response, so switching needs no refetch.
  const lineData = useMemo(
    () =>
      response
        ? bucketRowsToLinePoints(response.series, scoreMetric)
        : { dataPoints: [], criteriaNames: [] },
    [response, scoreMetric],
  );
  // Per-criterion score-distribution histograms — same ungrouped response.
  const histogramSeries = useMemo(
    () => (response ? bucketRowsToHistograms(response.series) : []),
    [response],
  );
  const histogramBins = response?.histogram_bins ?? 10;
  // criteria → evaluator-name label; charts key by criteria but display this.
  const criteriaLabels = useMemo(
    () => buildCriteriaLabelMap(response?.series ?? []),
    [response],
  );
  // Per-criterion averages as a single-column heatmap → the scorecard the
  // GroupBySection shows by default (before any group-by is chosen).
  const ungroupedHeatmap = useMemo(
    () => (response ? bucketRowsToUngroupedHeatmap(response.series) : EMPTY_HEATMAP),
    [response],
  );

  const groupedFacets = useMemo(
    () =>
      groupedResponse
        ? bucketRowsToGroupedLinePoints(groupedResponse.series)
        : [],
    [groupedResponse],
  );
  const groupColors = useMemo(
    () =>
      groupColorMap(groupedFacets.flatMap((f) => f.groupNames)),
    [groupedFacets],
  );
  const heatmapData = useMemo(
    () =>
      groupedResponse
        ? bucketRowsToHeatmapCells(groupedResponse.series)
        : EMPTY_HEATMAP,
    [groupedResponse],
  );
  const groupedCriteriaLabels = useMemo(
    () => buildCriteriaLabelMap(groupedResponse?.series ?? []),
    [groupedResponse],
  );
  const groupedBucket = groupedResponse?.bucket ?? "day";

  // Triage rail: result groups failing too often, ranked worst-first. Follows
  // the active grouping — per-(criterion × segment) once a group-by is chosen,
  // else the ungrouped per-criterion scores — and reads severity off each cell's
  // server-side failure count (below ATTENTION_SCORE_THRESHOLD, the same value
  // both fetches send), so "needs attention" counts failing results rather than
  // asking whether an average happened to fall under the line (#1492). A flat
  // sample floor suppresses cells too small to carry a rate. The rail names its
  // segments the same way the charts do (#1410): the label is the tag value's
  // display name, while the issue still drills on the raw value.
  const { aliases: tagAliases } = useTagAliases();
  const triageIssues = useMemo(() => {
    const data = groupByKey ? heatmapData : ungroupedHeatmap;
    const labels = groupByKey ? groupedCriteriaLabels : criteriaLabels;
    return deriveIssues(data, {
      threshold: ATTENTION_SCORE_THRESHOLD,
      criticalRate: ATTENTION_FAIL_RATE_CRITICAL,
      watchRate: ATTENTION_FAIL_RATE_WATCH,
      minCount: MIN_ISSUE_EVALUATIONS,
      labels,
      tagAliases,
      groupByKey,
    });
  }, [
    groupByKey,
    heatmapData,
    ungroupedHeatmap,
    groupedCriteriaLabels,
    criteriaLabels,
    tagAliases,
  ]);
  const triageLoading = groupByKey ? groupedLoading : aggregateLoading;

  // Headline KPIs. Overall score + evaluations come from the ungrouped
  // response; needs-attention reuses the triage issues so the strip and the
  // rail agree; the WoW delta + biggest regression come from a prior-period
  // fetch inside the hook.
  const kpis = useInsightsKpis({
    response,
    issues: triageIssues,
    labels: criteriaLabels,
    filters,
    criteriaFilter,
    dateRangeFilter,
    scoreFilter,
  });

  const bucket = response?.bucket ?? "day";
  const hasNoData =
    !aggregateLoading &&
    !aggregateError &&
    response !== null &&
    response.total_evaluations === 0;

  // Chart drill-down: seed the Traces slice with the clicked dimension (starting
  // from the active Insights filters) and jump to /traces (#1402).
  const navigate = useNavigate();
  const handleDrill = useCallback(
    (click: DrillClick) => {
      const target = buildDrillDownTarget(
        { filters, dateWindow, scoreFilter },
        click,
      );
      seedTracesFilters(target);
      navigate("/traces");
    },
    [filters, dateWindow, scoreFilter, navigate],
  );

  if (loadingMetadataKeys) {
    return (
      <div className="space-y-6">
        <div className="bg-card rounded-lg shadow-sm border border-border p-5 flex flex-wrap gap-3">
          {[150, 90, 90, 110, 90].map((w, i) => (
            <Skeleton key={i} height={32} width={w} className="rounded-full" />
          ))}
        </div>
        <ChartSkeleton variant="line" showTitle />
        <ChartSkeleton variant="bar" showTitle />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <SavedViewTabs
        current={{
          filters,
          criteriaFilter,
          dateWindow,
          scoreFilter,
          groupByKey,
          scoreMetric,
        }}
        onApply={applyView}
        namespace={INSIGHTS_NAMESPACE}
        resolvedRange={dateRangeFilter}
      />
      {isWindowStale && (
        <WindowStaleNotice
          dateWindow={dateWindow}
          showing={dateRangeFilter}
          onRefresh={refreshWindow}
        />
      )}
      <FilterPanel
        metadataKeys={metadataKeys}
        filters={filters}
        onAddFilter={addFilter}
        onRemoveFilter={removeFilter}
        onUpdateFilter={updateFilter}
        metadataValues={metadataValues}
        loadMetadataValuesForKey={loadMetadataValuesForKey}
        criteriaFilter={criteriaFilter}
        criteria={criteria}
        loadingCriteria={loadingCriteria}
        onCriteriaFilterChange={setCriteriaFilter}
        onLoadCriteria={loadCriteria}
        dateWindow={dateWindow}
        onDateWindowChange={setDateWindow}
        scoreFilter={scoreFilter}
        onScoreFilterChange={setScoreFilter}
      />

      {(metadataError || aggregateError || groupedError) && (
        <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 rounded">
          {metadataError || aggregateError || groupedError}
        </div>
      )}

      {truncation.isTruncated && (
        <TruncationWarning causes={truncation.causes} />
      )}

      {hasNoData ? (
        <div className="bg-card rounded-lg shadow-sm border border-border">
          <EmptyState
            message="No evaluation data found"
            description="Try adjusting your filters or check back later. Evaluation data will appear here once you start using the evaluation endpoints."
            icon={ChartBarIcon}
            size="lg"
            variant="info"
            actionLabel="View integration docs →"
            onAction={() => window.open(DOCS_LINK, "_blank", "noopener,noreferrer")}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Headline KPIs across the top — the one-glance portfolio answer. */}
          <KpiStrip kpis={kpis} loading={aggregateLoading} />

          {/* Charts column + a sticky triage rail; stacks to one column on narrow
              screens. min-w-0 keeps the responsive charts from blowing out the grid. */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
          <div className="space-y-6 min-w-0">
            {/* Heatmap / group-by leads: it's the portfolio-health view (#1411).
                Score Trends and the distribution follow. */}
            <GroupBySection
              metadataKeys={metadataKeys}
              groupByKey={groupByKey}
              onGroupByKeyChange={setGroupByKey}
              groupLimit={groupLimit}
              onGroupLimitChange={setGroupLimit}
              facets={groupedFacets}
              heatmapData={heatmapData}
              criteriaLabels={groupedCriteriaLabels}
              bucket={groupedBucket}
              groupColors={groupColors}
              loading={groupedLoading}
              truncationCauses={groupedTruncation.causes}
              scorecardData={ungroupedHeatmap}
              scorecardLabels={criteriaLabels}
              scorecardLoading={aggregateLoading}
              onDrill={handleDrill}
            />
            <EvaluationsChart
              dataPoints={lineData.dataPoints}
              criteriaNames={lineData.criteriaNames}
              labels={criteriaLabels}
              bucket={bucket}
              loading={aggregateLoading}
              metric={scoreMetric}
              onMetricChange={setScoreMetric}
              onDrill={handleDrill}
            />
            <RidgelineChart
              series={histogramSeries}
              binCount={histogramBins}
              labels={criteriaLabels}
              loading={aggregateLoading}
              onDrill={handleDrill}
            />
          </div>
          <TriageRail
            issues={triageIssues}
            loading={triageLoading}
            onDrill={handleDrill}
            className="lg:sticky lg:top-4"
          />
          </div>
        </div>
      )}
    </div>
  );
}
